import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './Account.css';

const AccountPage = () => {
  const { user, updateUserProfile } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await updateUserProfile({
        displayName: formData.displayName
      });
      
      setMessage({
        type: 'success',
        text: 'Profile updated successfully!'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({
        type: 'danger',
        text: error.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.get('/api/auth/export-data', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Create and download JSON file
      const dataStr = JSON.stringify(response.data.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `affsimple-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setMessage({
        type: 'success',
        text: 'Your data has been exported and downloaded successfully.'
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      setMessage({
        type: 'danger',
        text: 'Failed to export data. Please try again.'
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmEmail !== user.email) {
      setMessage({
        type: 'danger',
        text: 'Email confirmation does not match your account email.'
      });
      return;
    }

    setDeleteLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.delete('/api/auth/delete-account', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        data: {
          confirmEmail: confirmEmail
        }
      });

      setShowDeleteModal(false);
      setMessage({
        type: 'warning',
        text: `Account deletion scheduled. You have 30 days to cancel by logging in. Deletion date: ${new Date(response.data.data.deletionDate).toLocaleDateString()}`
      });
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Failed to schedule account deletion. Please try again.'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="account-page">
      <div className="page-header">
        <h1>Account Settings</h1>
        <p>Manage your account information and preferences</p>
      </div>

      <div className="content-section">
        <Card className="mb-4">
          <Card.Header className="bg-white">
            <h4>Profile Information</h4>
          </Card.Header>
          <Card.Body>
            {message.text && (
              <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
                {message.text}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Display Name</Form.Label>
                <Form.Control
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="custom-form-control"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  disabled
                  readOnly
                  className="custom-form-control"
                />
                <Form.Text className="text-muted">
                  Email cannot be changed directly. Contact support for assistance.
                </Form.Text>
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={loading} className="action-button">
                  {loading ? 'Updating...' : 'Update Profile'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="bg-white">
            <h4>Privacy & Data Controls</h4>
          </Card.Header>
          <Card.Body>
            <p>
              Under GDPR, you have the right to access, export, and delete your personal data.
              Use the controls below to manage your data.
            </p>

            <div className="mb-4">
              <h5>Export Your Data</h5>
              <p className="text-muted">
                Download all your personal data including profile, links, clicks, and conversions in JSON format.
              </p>
              <Button 
                variant="outline-primary" 
                onClick={handleExportData}
                disabled={exportLoading}
                className="me-2"
              >
                {exportLoading ? 'Exporting...' : 'Download My Data'}
              </Button>
            </div>

            <Alert variant="info">
              <strong>Data Controller:</strong> Skjoldan Consulting (CVR: DK21417289)<br />
              <strong>Contact:</strong> <a href="mailto:support@affsimple.com">support@affsimple.com</a><br />
              <strong>Supervisory Authority:</strong> <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer">Danish Data Protection Authority</a>
            </Alert>
          </Card.Body>
        </Card>

        <Card className="mb-4 danger-zone">
          <Card.Header className="bg-white">
            <h4 className="text-danger">Delete Account</h4>
          </Card.Header>
          <Card.Body>
            <p>
              Permanently delete your account and all associated data. This action cannot be undone.
              You will have a 30-day grace period to cancel the deletion by logging in.
            </p>
            
            <Alert variant="warning">
              <p className="mb-0">
                <strong>Warning:</strong> Before deleting your account, consider downloading your data using the
                "Download My Data" button above.
              </p>
            </Alert>
            
            <div className="d-flex justify-content-end mt-4">
              <Button 
                variant="outline-danger" 
                onClick={() => setShowDeleteModal(true)}
              >
                Request Account Deletion
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Account Deletion Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">Confirm Account Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">
              <strong>This action will permanently delete your account!</strong>
            </Alert>
            
            <p>
              To confirm account deletion, please type your email address below:
            </p>
            
            <Form.Group className="mb-3">
              <Form.Label>Email Confirmation</Form.Label>
              <Form.Control
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={user?.email}
                className="custom-form-control"
              />
            </Form.Group>
            
            <p className="text-muted small">
              Your account will be scheduled for deletion with a 30-day grace period.
              You can cancel the deletion by logging in during this period.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteAccount}
              disabled={deleteLoading || confirmEmail !== user?.email}
            >
              {deleteLoading ? 'Deleting...' : 'Delete My Account'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default AccountPage;
